import { Model } from "@alphascriptsolutions/client-core";
import { UICheckboxField, UIBinaryRadio } from "@alphascriptsolutions/client-core";
import { logger } from "@/helpers/tslog";
import { generateSourceAndNotesFromLLMData as generateFromLLM } from "@/utils/alphaAgent/generateSourceAndNotes";

// Local storage keys
export const STORAGE_KEYS = {
  // Title/Subtitle
  PLACEHOLDER_TITLE: "selectedPlaceholderTitle",
  PLACEHOLDER_SUBTITLE: "selectedPlaceholderSubTitle",
  TITLE_ENABLED: "selectedTitleEnabled",
  SUBTITLE_ENABLED: "selectedSubTitleEnabled",
  
  // Source/Notes
  SOURCE_ENABLED: "selectedSourceEnabled",
  SOURCE_VALUE: "selectedSourceValue",
  NOTES_ENABLED: "selectedNotesEnabled",
  NOTES_VALUE: "selectedNotesValue",
  
  // Positioning
  SOURCE_POSITIONING: "selectedSourcePositioning",
  NOTES_POSITIONING: "selectedNotesPositioning",
  
  // Other
  ALPHA_CREATE_DATA: "alphaCreateData",
  TRADING_VOLUME: "tradingVolume",
} as const;

// Utility functions for localStorage operations
export const localStorageUtils = {
  get: (key: string): string | null => localStorage.getItem(key),
  set: (key: string, value: string): void => localStorage.setItem(key, value),
  getBoolean: (key: string): boolean | null => {
    const value = localStorage.getItem(key);
    return value === null ? null : value === "true";
  },
  setBoolean: (key: string, value: boolean): void => localStorage.setItem(key, value.toString()),
};

// Field restoration utilities
export const fieldRestorationUtils = {
  restoreTitleSubtitle: (model: Model, setModel: (model: Model) => void) => {
    const placeholderTitle = model.getByName<UICheckboxField>("placeholderTitleField");
    const placeholderSubTitle = model.getByName<UICheckboxField>("placeholderSubTitleField");
    
    const savedTitle = localStorageUtils.get(STORAGE_KEYS.PLACEHOLDER_TITLE);
    const savedSubTitle = localStorageUtils.get(STORAGE_KEYS.PLACEHOLDER_SUBTITLE);
    const savedTitleEnabled = localStorageUtils.getBoolean(STORAGE_KEYS.TITLE_ENABLED);
    const savedSubTitleEnabled = localStorageUtils.getBoolean(STORAGE_KEYS.SUBTITLE_ENABLED);
    const rawCreate = localStorageUtils.get(STORAGE_KEYS.ALPHA_CREATE_DATA);
    const parsedCreate = rawCreate ? (() => { try { return JSON.parse(rawCreate); } catch { return null; } })() : null;
    const fallbackTitle = parsedCreate?.chartTitle ?? "";
    const fallbackSubTitle = parsedCreate?.chartSubtitleLeft ?? "";    
    // Restore title
    if (savedTitle && placeholderTitle) {
      placeholderTitle.value = savedTitle;
    } else if (placeholderTitle) {
      // Fallback to alphaCreateData title if available
      placeholderTitle.value = fallbackTitle;
    }

    if(savedTitleEnabled !== null) {
      placeholderTitle.isChecked = savedTitleEnabled;
    } else if (savedTitleEnabled === null) {
      placeholderTitle.isChecked = true;
    }
    
    
    // Restore subtitle
    if (savedSubTitle && placeholderSubTitle) {
      placeholderSubTitle.value = savedSubTitle;
    } else if (placeholderSubTitle) {
      // Fallback to alphaCreateData subtitle if available
      placeholderSubTitle.value = fallbackSubTitle;
    }
    
    if(savedSubTitleEnabled !== null) {
      placeholderSubTitle.isChecked = savedSubTitleEnabled;
    } else if (savedSubTitleEnabled === null) {
      placeholderSubTitle.isChecked = true;
    }
    
    // If both saved, restore positioning and return
    if (savedTitle && savedSubTitle) {
      fieldRestorationUtils.restorePositioning(model);
    }
      
    try { setModel(model); } catch (_) {}
  },

  restoreSourceAndNotes: async (model: Model, setModel: (model: Model) => void) => {
    const formatTemplateSource = model.getByName<UICheckboxField>("formatTemplateSourceField");
    const formatTemplateNotes = model.getByName<UICheckboxField>("formatTemplateNotesField");
    
    const savedSourceEnabled = localStorageUtils.getBoolean(STORAGE_KEYS.SOURCE_ENABLED);
    const savedSourceValue = localStorageUtils.get(STORAGE_KEYS.SOURCE_VALUE);
    const savedNotesEnabled = localStorageUtils.getBoolean(STORAGE_KEYS.NOTES_ENABLED);
    const savedNotesValue = localStorageUtils.get(STORAGE_KEYS.NOTES_VALUE);
    const llmData = await generateSourceAndNotesFromLLMData();
    
    // Restore source
    if (savedSourceValue && formatTemplateSource) {
      formatTemplateSource.value = savedSourceValue; 
    } else if (formatTemplateSource) {
      formatTemplateSource.value = llmData.source;
    }

    if (savedSourceEnabled !== null) {
      formatTemplateSource.isChecked = savedSourceEnabled;
    } else if (savedSourceEnabled === null) {
      formatTemplateSource.isChecked = true;
    }
    
    // Restore notes
    if (savedNotesValue && formatTemplateNotes) {
      formatTemplateNotes.value = savedNotesValue;
    } else if (formatTemplateNotes) {
      formatTemplateNotes.value = llmData.notes.join("\n");
    }

    if (savedNotesEnabled !== null) {
      formatTemplateNotes.isChecked = savedNotesEnabled;
    } else if (savedNotesEnabled === null) {
      formatTemplateNotes.isChecked = true;
    }
    
    // If both saved, restore positioning and return
    if (savedSourceValue && savedNotesValue) {
      fieldRestorationUtils.restorePositioning(model);
    }
    try { setModel(model); } catch (_) {}
  },

  restorePositioning: (model: Model) => {
    const positioningChoiceSource = model.getByName<UIBinaryRadio>("positioningChoiceSource");
    const positioningChoiceNotes = model.getByName<UIBinaryRadio>("positioningChoiceNotes");
    
    const savedSourcePositioning = localStorageUtils.get(STORAGE_KEYS.SOURCE_POSITIONING);
    const savedNotesPositioning = localStorageUtils.get(STORAGE_KEYS.NOTES_POSITIONING);
    
    if (savedSourcePositioning && positioningChoiceSource) {
      positioningChoiceSource.value = savedSourcePositioning;
    }
    
    if (savedNotesPositioning && positioningChoiceNotes) {
      positioningChoiceNotes.value = savedNotesPositioning;
    }
  },

  // AlphaEdit: Restore Title/SubTitle using priority:
  // 1) Saved values (localStorage)
  // 2) documentContent.metadata (if provided by caller)
  // 3) alphaCreateData fallback
  restoreTitleSubtitleAlphaEdit: (
    model: Model,
    setModel: (model: Model) => void,
    documentContent?: any
  ) => {
    const placeholderTitle = model.getByName<UICheckboxField>("placeholderTitleField");
    const placeholderSubTitle = model.getByName<UICheckboxField>("placeholderSubTitleField");

    const savedTitle = localStorageUtils.get(STORAGE_KEYS.PLACEHOLDER_TITLE);
    const savedSubTitle = localStorageUtils.get(STORAGE_KEYS.PLACEHOLDER_SUBTITLE);
    const savedTitleEnabled = localStorageUtils.getBoolean(STORAGE_KEYS.TITLE_ENABLED);
    const savedSubTitleEnabled = localStorageUtils.getBoolean(STORAGE_KEYS.SUBTITLE_ENABLED);

    const rawCreate = localStorageUtils.get(STORAGE_KEYS.ALPHA_CREATE_DATA);
    const parsedCreate = rawCreate ? (() => { try { return JSON.parse(rawCreate); } catch { return null; } })() : null;

    const alphaCreateTitle = parsedCreate?.chartTitle ?? "";
    const alphaCreateSubTitle = parsedCreate?.chartSubtitleLeft ?? "";
    const docMetaTitle = documentContent?.metadata?.chartTitle?.value ?? documentContent?.metadata?.chartTitle ?? "";
    const docMetaSubTitle = documentContent?.metadata?.chartSubTitle?.value ?? documentContent?.metadata?.chartSubTitle ?? "";
    const docMetaTitleEnabled = documentContent?.metadata?.chartTitle?.enabled ;
    const docMetaSubTitleEnabled = documentContent?.metadata?.chartSubTitle?.enabled ;
    // Restore title
    if (savedTitle && placeholderTitle) {
      placeholderTitle.value = savedTitle;
    } else if (placeholderTitle && alphaCreateTitle) {
      placeholderTitle.value = alphaCreateTitle;
    } else if (placeholderTitle && docMetaTitle) {
      placeholderTitle.value = docMetaTitle;
    }

    if (savedSubTitle && placeholderSubTitle) {
      placeholderSubTitle.value = savedSubTitle;
    } else if (placeholderSubTitle && alphaCreateSubTitle) {
      placeholderSubTitle.value = alphaCreateSubTitle;
    } else if (placeholderSubTitle && docMetaSubTitle) {
      placeholderSubTitle.value = docMetaSubTitle;
    }

    if (savedTitleEnabled !== null) {
      placeholderTitle.isChecked = savedTitleEnabled;
    } else if (savedTitleEnabled === null) {
      placeholderTitle.isChecked = docMetaTitleEnabled;
    }

    if (savedSubTitleEnabled !== null) {
      placeholderSubTitle.isChecked = savedSubTitleEnabled;
    } else if (savedSubTitleEnabled === null) {
      placeholderSubTitle.isChecked = docMetaSubTitleEnabled;
    }
    
    

    try { setModel(model); } catch {}
  },

  // AlphaEdit: Restore Source/Notes (checkbox enablement and persist values)
  // Priority for enablement: saved > documentContent > datasource
  // Priority for values: saved > documentContent.metadata > alphaCreateData fallback
  restoreSourceNotesAlphaEdit: async (
    model: Model,
    setModel: (model: Model) => void,
    documentContent?: any,
    datasource?: { formatTemplateSource?: any; formatTemplateNotes?: any }
  ) => {
    const formatTemplateSource = model.getByName<UICheckboxField>("formatTemplateSourceField");
    const formatTemplateNotes = model.getByName<UICheckboxField>("formatTemplateNotesField");

    const savedSourceEnabled = localStorageUtils.getBoolean(STORAGE_KEYS.SOURCE_ENABLED);
    const savedNotesEnabled = localStorageUtils.getBoolean(STORAGE_KEYS.NOTES_ENABLED);
    const docMetaSourceEnabled = documentContent?.metadata?.source?.enabled;
    const docMetaNotesEnabled = documentContent?.metadata?.notes?.enabled;
    const savedSourceValue = localStorageUtils.get(STORAGE_KEYS.SOURCE_VALUE);
    const savedNotesValue = localStorageUtils.get(STORAGE_KEYS.NOTES_VALUE);

    const llmData = await generateSourceAndNotesFromLLMData();

    // Restore source
    if (savedSourceValue && formatTemplateSource) {
      formatTemplateSource.value = savedSourceValue;
    } else if (formatTemplateSource) {
      formatTemplateSource.value = llmData.source;
    }
    
    if (savedSourceEnabled !== null) {
      formatTemplateSource.isChecked = savedSourceEnabled;
    } else if (savedSourceEnabled === null) {
      formatTemplateSource.isChecked = docMetaSourceEnabled;
    }

    // Restore notes
    if (savedNotesValue && formatTemplateNotes) {
      formatTemplateNotes.value = savedNotesValue;
    } else if (formatTemplateNotes) {
      formatTemplateNotes.value = llmData.notes.join("\n");
    }

    if (savedNotesEnabled !== null) {
      formatTemplateNotes.isChecked = savedNotesEnabled;
    } else if (savedNotesEnabled === null) {
      formatTemplateNotes.isChecked = docMetaNotesEnabled;
    }
    


    try { setModel(model); } catch {}
  },
};

// Event handler utilities
export const eventHandlerUtils = {
  createSourceNotesHandlers: (formatTemplateSource: UICheckboxField, formatTemplateNotes: UICheckboxField) => {
    // Source handlers
    if (formatTemplateSource) {
      formatTemplateSource.onToggle = (checked: boolean) => {
        localStorageUtils.setBoolean(STORAGE_KEYS.SOURCE_ENABLED, checked);
      };
      
      formatTemplateSource.onChange = (event: any) => {
        localStorageUtils.set(STORAGE_KEYS.SOURCE_VALUE, event.target.value);
      };
    }
    
    // Notes handlers
    if (formatTemplateNotes) {
      formatTemplateNotes.onToggle = (checked: boolean) => {
        localStorageUtils.setBoolean(STORAGE_KEYS.NOTES_ENABLED, checked);
      };
      
      formatTemplateNotes.onChange = (event: any) => {
        localStorageUtils.set(STORAGE_KEYS.NOTES_VALUE, event.target.value);
      };
    }
  },

  createTitleSubtitleHandlers: (placeholderTitle: UICheckboxField, placeholderSubTitle: UICheckboxField) => {
    // Title handlers
    if (placeholderTitle) {
      placeholderTitle.onChange = (event: any) => {
        localStorageUtils.set(STORAGE_KEYS.PLACEHOLDER_TITLE, event.target.value);
      };
      
      placeholderTitle.onToggle = (checked: boolean) => {
        localStorageUtils.setBoolean(STORAGE_KEYS.TITLE_ENABLED, checked);
      };
    }
    
    // Subtitle handlers
    if (placeholderSubTitle) {
      placeholderSubTitle.onChange = (event: any) => {
        localStorageUtils.set(STORAGE_KEYS.PLACEHOLDER_SUBTITLE, event.target.value);
      };
      
      placeholderSubTitle.onToggle = (checked: boolean) => {
        localStorageUtils.setBoolean(STORAGE_KEYS.SUBTITLE_ENABLED, checked);
      };
    }
  },

  createPositioningHandlers: (positioningChoiceSource: UIBinaryRadio, positioningChoiceNotes: UIBinaryRadio) => {
    if (positioningChoiceSource) {
      positioningChoiceSource.onChange = (value: string) => {
        localStorageUtils.set(STORAGE_KEYS.SOURCE_POSITIONING, value);
      };
    }
    
    if (positioningChoiceNotes) {
      positioningChoiceNotes.onChange = (value: string) => {
        localStorageUtils.set(STORAGE_KEYS.NOTES_POSITIONING, value);
      };
    }
  },

  // AlphaEdit variants for UICheckbox components (no text value input fields)
  // Persist enabled state for Source/Notes based on selection presence
  createAlphaEditSourceNotesHandlers: (formatTemplateSource: any, formatTemplateNotes: any) => {
    if (formatTemplateSource) {
      formatTemplateSource.onChange = (value: any[]) => {
        const enabled = Array.isArray(value) && value.length > 0;
        localStorageUtils.setBoolean(STORAGE_KEYS.SOURCE_ENABLED, enabled);
      };
    }
    if (formatTemplateNotes) {
      formatTemplateNotes.onChange = (value: any[]) => {
        const enabled = Array.isArray(value) && value.length > 0;
        localStorageUtils.setBoolean(STORAGE_KEYS.NOTES_ENABLED, enabled);
      };
    }
  },
};

// Helper function to generate source and notes from LLM data
export const generateSourceAndNotesFromLLMData = () => {
  try {
    const rawCreate = localStorageUtils.get(STORAGE_KEYS.ALPHA_CREATE_DATA);
    if (!rawCreate) {
      logger.info("No alphaCreateData found for source and notes generation");
      return { source: "", notes: [] };
    }

    const llmGlobalStateData = JSON.parse(rawCreate);
    return generateFromLLM(llmGlobalStateData);
  } catch (error) {
    logger.error("Error generating source and notes from LLM data:", error);
    return { source: "", notes: [] };
  }
};

// Returns only the raw values for title, subtitle, source, and notes without mutating any UI/model state
// Priority:
// - Title/SubTitle: saved (localStorage) > alphaCreateData > documentContent.metadata > ""
// - Source/Notes: saved (localStorage) > generated from LLM (alphaCreateData) > ""
export const getFormatAndPlaceholderValues = () => {
  try {
    
    // Parse alphaCreateData for fallbacks
    const rawCreate = localStorageUtils.get(STORAGE_KEYS.ALPHA_CREATE_DATA);
    const parsedCreate = rawCreate ? JSON.parse(rawCreate) : null;

    const alphaCreateTitle = parsedCreate?.chartTitle || "";
    const alphaCreateSubTitle = parsedCreate?.chartSubtitleLeft || "";

    // Generate LLM-based source/notes if no saved values
    const llmData = generateSourceAndNotesFromLLMData();

    const title = alphaCreateTitle || "";
    const subTitle = alphaCreateSubTitle || "";
    const source = llmData.source || "";
    const notes = llmData.notes.join("\n");

    return { title, subTitle, source, notes };
  } catch {
    return { title: "", subTitle: "", source: "", notes: "" };
  }
};